import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api, type MailboxProgress } from '@/api/client'
import { MigrationProgressBar } from './MigrationProgressBar'
import { X, AlertTriangle, CheckCircle2, Loader2, Download, Clock, Mail, Archive, Folder, RotateCcw, CheckSquare } from 'lucide-react'
import clsx from 'clsx'

interface Props {
  projectId: string
  waveItemId: string | null
  /** Progresso opcional, pra evitar segunda chamada — vem do MigrationProgressResponse */
  progress?: MailboxProgress
  onClose: () => void
}

interface PassExecution {
  passOrder: number
  startedAt: string
  completedAt: string | null
  itemsAttempted: number
  itemsSucceeded: number
  itemsFailed: number
  itemsSkipped: number
  bytesTransferred: number
  parallelism: number
  delayBetweenSavesMs: number
  status: number
  errorDetail: string | null
}

interface MessageFailure {
  id: string
  internetMessageId: string
  subject: string | null
  folderName: string
  failureType: number
  errorDetail: string | null
  passOrder: number
  resolved: boolean
  detectedAt: string
  resolvedAt: string | null
  resolvedInPassOrder: number | null
}

interface TaskDetail {
  taskType: number
  status: number
  transferredItems: number | null
  transferredBytes: number | null
  errorMessage: string | null
  startedAt: string | null
  completedAt: string | null
}

interface ItemDetail {
  item: {
    id: string
    waveId: string
    sourceUpn: string
    destinationUpn: string | null
    status: number
    startedAt: string | null
    completedAt: string | null
    errorSummary: string | null
    retryCount: number
    reconciliationStatus: number | null
  }
  estimatedItems: number
  estimatedBytes: number
  tasks: TaskDetail[]
  passes: PassExecution[]
  failures: MessageFailure[]
  failuresShown: number
  totalFailures: number
  unresolvedFailures: number
  quarantineCount: number
}

// Espelha o enum FailureType do domínio (M365Transfer.Domain.Planning.FailureType) — o front estava
// com um mapa totalmente trocado (rotulava ServerBusy=1 como "Transient", etc.).
const FAILURE_TYPE_NAMES: Record<number, string> = {
  0: 'Desconhecido',
  1: 'Limite temporário (throttle)',
  2: 'Token expirado',
  3: 'Caixa temporariamente indisponível',
  4: 'Timeout',
  5: 'Não encontrada (origem deletou)',
  6: 'Corrompida (MIME inválido)',
  7: 'Sem espaço no destino',
  8: 'Erro de rede',
  10: 'Mensagem grande demais',
  11: 'Anexo bloqueado',
  12: 'Conteúdo inválido',
  13: 'Caracteres inválidos',
  14: 'Tipo não suportado',
  15: 'Persistentemente faltante',
  16: 'Faltante (re-tentando)',
  99: 'Outro',
}

// Permanentes/terminais: vão pra quarentena ou desistência tipada → exigem atenção.
// (a classificação transitório vs atenção vem do backend, nos campos transientFailures/attentionFailures do progress)
const PERMANENT_FAILURE_TYPES = new Set([6, 10, 11, 12, 13, 14, 15])

const TASK_TYPE_NAMES: Record<number, string> = {
  3: 'Email (Outlook)',
  4: 'OneDrive',
  5: 'Calendário (Outlook)',
  6: 'Contatos (Outlook)',
  8: 'Tarefas (Outlook / To Do)',
}

const TASK_TYPE_ICONS: Record<number, React.ReactNode> = {
  3: <Mail className="h-4 w-4" />,
  4: <Folder className="h-4 w-4" />,
  5: <Clock className="h-4 w-4" />,
  6: <Folder className="h-4 w-4" />,
  8: <CheckSquare className="h-4 w-4" />,
}

function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B'
  const units = ['B', 'KB', 'MB', 'GB', 'TB']
  const i = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1)
  const v = bytes / Math.pow(1024, i)
  return `${v.toFixed(v < 10 ? 1 : 0)} ${units[i]}`
}

function formatDuration(startIso: string, endIso: string | null): string {
  if (!endIso) return 'em andamento'
  const ms = new Date(endIso).getTime() - new Date(startIso).getTime()
  const s = Math.floor(ms / 1000)
  if (s < 60) return `${s}s`
  const m = Math.floor(s / 60)
  const sec = s % 60
  if (m < 60) return `${m}min ${sec}s`
  const h = Math.floor(m / 60)
  return `${h}h ${m % 60}min`
}

// WaveItemStatus enum
const STATUS_LABELS: Record<number, string> = {
  1: 'Pendente', 2: 'Executando', 3: 'Limitado', 4: 'Concluído', 5: 'Falhou', 6: 'Pulado', 7: 'Rodada concluída', 8: 'Pausado',
  9: 'Aguardando permissão EWS', 10: 'Timeout permissão EWS'
}

// MigrationTaskStatus enum (diferente do WaveItemStatus!)
const TASK_STATUS_LABELS: Record<number, string> = {
  1: 'Pendente', 2: 'Executando', 3: 'Concluído', 4: 'Falhou', 5: 'Pulado'
}

// Cores específicas por status de task
const TASK_STATUS_COLORS: Record<number, string> = {
  1: 'text-slate-500',
  2: 'text-amber-600',
  3: 'text-green-700',
  4: 'text-red-700',
  5: 'text-blue-600',  // Skipped - cor informativa, não erro
}

export function MailboxDetailDrawer({ projectId, waveItemId, progress, onClose }: Props) {
  const [failureFilter, setFailureFilter] = useState<'all' | 'open' | 'quarantine' | 'resolved'>('all')
  const qc = useQueryClient()

  const retryTaskMut = useMutation({
    mutationFn: (taskType: number) => api.post(`/api/projects/${projectId}/migration/items/${waveItemId}/tasks/${taskType}/retry`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['mailbox-detail', projectId, waveItemId] }),
  })

  // Cancela uma task falhada (regra UX OneDrive não-provisionado): marca Skipped e, se sobra tudo feito,
  // o backend promove o usuário a Concluído.
  const skipTaskMut = useMutation({
    mutationFn: (taskType: number) => api.post(`/api/projects/${projectId}/migration/items/${waveItemId}/tasks/${taskType}/skip`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['item-detail', projectId, waveItemId] })
      qc.invalidateQueries({ queryKey: ['execution-status', projectId] })
      qc.invalidateQueries({ queryKey: ['wave-items'] })
    },
  })

  const retryEwsMut = useMutation({
    mutationFn: () => api.post(`/api/projects/${projectId}/migration/items/${waveItemId}/retry-ews-wait`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['item-detail', projectId, waveItemId] })
      qc.invalidateQueries({ queryKey: ['execution-status', projectId] })
      qc.invalidateQueries({ queryKey: ['wave-items'] })
    },
  })

  const { data, isLoading } = useQuery({
    queryKey: ['item-detail', projectId, waveItemId],
    queryFn: () => api.get<ItemDetail>(`/api/projects/${projectId}/migration/items/${waveItemId}/detail`),
    enabled: !!waveItemId,
    refetchInterval: 5000,
  })

  if (!waveItemId) return null

  // Filter failures
  const filteredFailures = data?.failures.filter((f) => {
    if (failureFilter === 'all') return true
    if (failureFilter === 'open') return !f.resolved
    if (failureFilter === 'quarantine') return PERMANENT_FAILURE_TYPES.has(f.failureType)
    if (failureFilter === 'resolved') return f.resolved
    return true
  }) ?? []

  function downloadEml(failureId: string, messageId: string) {
    // Endpoint quarantine usa hash de messageId; nosso endpoint usa o próprio messageId
    const encoded = encodeURIComponent(messageId)
    window.open(`/api/projects/${projectId}/migration/quarantine/${waveItemId}/${encoded}`, '_blank')
  }

  return (
    <div className="fixed inset-0 z-50 flex">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-slate-900/40"
        onClick={onClose}
      />

      {/* Drawer */}
      <div className="ml-auto relative w-full max-w-3xl bg-white shadow-2xl overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between z-10">
          <div className="min-w-0 flex-1">
            <h2 className="text-lg font-semibold truncate">
              {data?.item.sourceUpn || 'Carregando...'}
            </h2>
            <p className="text-xs text-slate-500 truncate">
              → {data?.item.destinationUpn || '(sem destino)'}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="ml-3 p-1 hover:bg-slate-100 rounded text-slate-500"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {isLoading && (
          <div className="p-12 text-center text-slate-500">
            <Loader2 className="h-6 w-6 animate-spin mx-auto mb-2" />
            Carregando detalhes...
          </div>
        )}

        {data && (
          <div className="p-6 space-y-6">
            {/* Summary */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="bg-slate-50 rounded p-3">
                <div className="text-xs text-slate-500">Status</div>
                <div className="font-semibold">{STATUS_LABELS[data.item.status] || `?(${data.item.status})`}</div>
              </div>
              <div className="bg-slate-50 rounded p-3">
                <div className="text-xs text-slate-500">Migrado</div>
                <div className="font-semibold">{(progress?.transferredItems ?? 0).toLocaleString()} msgs</div>
                <div className="text-xs text-slate-400">{formatBytes(progress?.transferredBytes ?? 0)}</div>
              </div>
              <div className="bg-slate-50 rounded p-3">
                <div className="text-xs text-slate-500">Falhas pendentes</div>
                <div className="font-semibold text-amber-700">
                  {(data.unresolvedFailures ?? data.failures.filter((f) => !f.resolved).length).toLocaleString()}
                </div>
                <div className="text-xs text-slate-400">
                  de {(data.totalFailures ?? data.failures.length).toLocaleString()} registradas
                  {data.totalFailures > data.failuresShown && ` (${data.failuresShown} exibidas)`}
                </div>
                {(progress?.attentionFailures ?? 0) > 0 && (
                  <div className="text-xs text-red-700 mt-0.5">{progress!.attentionFailures} de atenção</div>
                )}
                {(progress?.transientFailures ?? 0) > 0 && (
                  <div className="text-xs text-sky-700 mt-0.5">
                    {progress!.transientFailures} transitórias (re-tentadas automaticamente)
                  </div>
                )}
              </div>
              <div className="bg-slate-50 rounded p-3">
                <div className="text-xs text-slate-500">Em quarentena</div>
                <div className="font-semibold text-red-700">{data.quarantineCount}</div>
              </div>
            </div>

            {/* Progress bar */}
            {progress && (
              <div>
                <h3 className="text-sm font-semibold mb-2">Progresso</h3>
                <MigrationProgressBar progress={progress} />
              </div>
            )}

            {/* Error summary se houver */}
            {data.item.errorSummary && (
              <div className={clsx(
                'border rounded p-3',
                data.item.status === 10 ? 'bg-orange-50 border-orange-200' : 'bg-red-50 border-red-200',
              )}>
                <div className="flex items-start gap-2">
                  {data.item.status === 10 ? (
                    <Clock className="h-4 w-4 text-orange-600 shrink-0 mt-0.5" />
                  ) : (
                    <AlertTriangle className="h-4 w-4 text-red-600 shrink-0 mt-0.5" />
                  )}
                  <div className="flex-1">
                    <div className={clsx('font-semibold text-sm', data.item.status === 10 ? 'text-orange-900' : 'text-red-900')}>
                      {data.item.status === 10 ? 'Timeout aguardando permissão EWS' : 'Erro registrado'}
                    </div>
                    <div className={clsx('text-xs mt-1 font-mono whitespace-pre-wrap', data.item.status === 10 ? 'text-orange-800' : 'text-red-800')}>
                      {data.item.errorSummary}
                    </div>
                    {data.item.status === 10 && (
                      <div className="mt-3">
                        <button
                          type="button"
                          disabled={retryEwsMut.isPending}
                          onClick={() => retryEwsMut.mutate()}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded bg-orange-600 hover:bg-orange-700 text-white disabled:opacity-50"
                        >
                          <RotateCcw className={clsx('h-3 w-3', retryEwsMut.isPending && 'animate-spin')} />
                          {retryEwsMut.isPending ? 'Resetando...' : 'Tentar novamente'}
                        </button>
                        {retryEwsMut.isSuccess && (
                          <span className="ml-2 text-xs text-green-700">
                            ✓ Resetado. Clique em "Continuar migração" na tela principal.
                          </span>
                        )}
                        {retryEwsMut.isError && (
                          <span className="ml-2 text-xs text-red-700">Erro ao resetar</span>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Tasks */}
            <div>
              <h3 className="text-sm font-semibold mb-2">Tarefas</h3>
              <div className="space-y-1">
                {data.tasks.map((t) => {
                  const noPending = (progress?.attentionFailures ?? 0) === 0 && (progress?.transientFailures ?? 0) === 0
                  // Email marcado "Falhou" mas SEM falha pendente e 100% migrado → na real concluiu (as
                  // transitórias daquela passada se resolveram); o status Failed é resíduo. Mostra "Concluído".
                  const calmDone = t.taskType === 3 && t.status === 4 && noPending
                    && (t.transferredItems ?? 0) > 0 && data.estimatedItems > 0
                    && (t.transferredItems ?? 0) >= data.estimatedItems
                  // Email "Falhou" mas as pendências são só transitórias (throttle etc.) e nenhuma de atenção
                  // → "Re-tentando" calmo, sem botão (re-tenta sozinho).
                  const calmTransient = t.taskType === 3 && t.status === 4
                    && (progress?.attentionFailures ?? 0) === 0 && (progress?.transientFailures ?? 0) > 0
                  return (
                  <div key={t.taskType} className="flex items-center gap-3 p-2 bg-slate-50 rounded text-sm">
                    {TASK_TYPE_ICONS[t.taskType] || <Mail className="h-4 w-4" />}
                    <span className="font-medium w-20">{TASK_TYPE_NAMES[t.taskType] || `Task ${t.taskType}`}</span>
                    <span className="flex-1 text-xs text-slate-600">
                      {t.transferredItems !== null && `${t.transferredItems.toLocaleString()} itens`}
                      {t.transferredBytes !== null && ` • ${formatBytes(t.transferredBytes)}`}
                    </span>
                    <span className={clsx('text-xs font-medium',
                      calmDone ? 'text-green-700' : calmTransient ? 'text-sky-700' : (TASK_STATUS_COLORS[t.status] || 'text-slate-500'))}>
                      {calmDone ? 'Concluído' : calmTransient ? 'Re-tentando' : (TASK_STATUS_LABELS[t.status] || `?(${t.status})`)}
                    </span>
                    {t.errorMessage && t.status === 4 && !calmDone && (
                      <div className="text-xs text-slate-500 italic flex-shrink-0 max-w-md truncate" title={t.errorMessage}>
                        {calmTransient
                          ? `${progress?.transientFailures} com limite temporário — re-tentando automaticamente`
                          : t.errorMessage}
                      </div>
                    )}
                    {t.status === 4 && !calmTransient && !calmDone && (
                      <button
                        type="button"
                        disabled={retryTaskMut.isPending}
                        onClick={() => retryTaskMut.mutate(t.taskType)}
                        className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded bg-orange-600 hover:bg-orange-700 text-white disabled:opacity-50"
                      >
                        <RotateCcw className={clsx('h-3 w-3', retryTaskMut.isPending && 'animate-spin')} />
                        Tentar novamente
                      </button>
                    )}
                    {/* OneDrive falhado (ex.: destino não provisionado / usuário sem OneDrive): cancelar a
                        tarefa. Marca Skipped e, se sobra tudo feito, promove o usuário a Concluído. */}
                    {t.taskType === 4 && t.status === 4 && (
                      <button
                        type="button"
                        disabled={skipTaskMut.isPending}
                        onClick={() => skipTaskMut.mutate(t.taskType)}
                        title="Desativa a transferência do OneDrive desta conta (usuário sem OneDrive)"
                        className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded bg-slate-200 hover:bg-slate-300 text-slate-700 disabled:opacity-50"
                      >
                        <X className={clsx('h-3 w-3', skipTaskMut.isPending && 'animate-pulse')} />
                        Cancelar tarefa
                      </button>
                    )}
                  </div>
                  )
                })}
              </div>
            </div>

            {/* Passadas */}
            <div>
              <h3 className="text-sm font-semibold mb-2">Passadas executadas ({data.passes.length})</h3>
              {data.passes.length === 0 ? (
                <p className="text-xs text-slate-500 italic">Nenhuma passada executada ainda</p>
              ) : (
                <div className="space-y-1">
                  {data.passes.map((p) => (
                    <div key={p.passOrder} className="border border-slate-200 rounded p-3">
                      <div className="flex items-center justify-between mb-1">
                        <div className="font-medium text-sm">
                          Passada {p.passOrder}
                          <span className="ml-2 text-xs text-slate-500">
                            P={p.parallelism}, delay={p.delayBetweenSavesMs}ms
                          </span>
                        </div>
                        <span className="text-xs text-slate-500">
                          {formatDuration(p.startedAt, p.completedAt)}
                        </span>
                      </div>
                      <div className="text-xs text-slate-600 space-x-3">
                        <span className="text-green-700">✓ {p.itemsSucceeded.toLocaleString()}</span>
                        {p.itemsFailed > 0 && (
                          <span className="text-red-700">✗ {p.itemsFailed.toLocaleString()}</span>
                        )}
                        {p.itemsSkipped > 0 && (
                          <span className="text-slate-500">↷ {p.itemsSkipped.toLocaleString()}</span>
                        )}
                        <span>•</span>
                        <span>{formatBytes(p.bytesTransferred)}</span>
                      </div>
                      {p.errorDetail && (
                        <div className="mt-1 text-xs text-red-700 font-mono">{p.errorDetail}</div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Falhas */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-semibold">Falhas registradas ({filteredFailures.length})</h3>
                <div className="flex gap-1 text-xs">
                  {(['all', 'open', 'quarantine', 'resolved'] as const).map((f) => (
                    <button
                      key={f}
                      type="button"
                      onClick={() => setFailureFilter(f)}
                      className={clsx(
                        'px-2 py-0.5 rounded',
                        failureFilter === f
                          ? 'bg-blue-100 text-blue-800 font-medium'
                          : 'text-slate-600 hover:bg-slate-100',
                      )}
                    >
                      {f === 'all' ? 'Todas' : f === 'open' ? 'Abertas' : f === 'quarantine' ? 'Quarentena' : 'Resolvidas'}
                    </button>
                  ))}
                </div>
              </div>

              {filteredFailures.length === 0 ? (
                <p className="text-xs text-slate-500 italic">Nenhuma falha {failureFilter !== 'all' ? `(filtro: ${failureFilter})` : 'registrada'}</p>
              ) : (
                <div className="space-y-1 max-h-96 overflow-y-auto">
                  {filteredFailures.map((f) => {
                    const isPermanent = PERMANENT_FAILURE_TYPES.has(f.failureType)
                    return (
                      <div
                        key={f.id}
                        className={clsx(
                          'border rounded p-2 text-xs',
                          f.resolved ? 'bg-green-50 border-green-200' :
                            isPermanent ? 'bg-red-50 border-red-200' :
                              'bg-amber-50 border-amber-200',
                        )}
                      >
                        <div className="flex items-start gap-2">
                          <div className="shrink-0 mt-0.5">
                            {f.resolved ? (
                              <CheckCircle2 className="h-3 w-3 text-green-600" />
                            ) : isPermanent ? (
                              <Archive className="h-3 w-3 text-red-600" />
                            ) : (
                              <AlertTriangle className="h-3 w-3 text-amber-600" />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="font-medium truncate">
                              {f.subject || '(sem subject)'}
                            </div>
                            <div className="text-slate-500 truncate">
                              {f.folderName} • {FAILURE_TYPE_NAMES[f.failureType] || `?(${f.failureType})`} • passada {f.passOrder}
                            </div>
                            {f.errorDetail && (
                              <div className="text-slate-600 mt-0.5 font-mono truncate">
                                {f.errorDetail}
                              </div>
                            )}
                            {f.resolved && f.resolvedInPassOrder && (
                              <div className="text-green-700 mt-0.5">
                                Resolvida na passada {f.resolvedInPassOrder}
                              </div>
                            )}
                          </div>
                          {isPermanent && (
                            <button
                              type="button"
                              onClick={() => downloadEml(f.id, f.internetMessageId)}
                              className="shrink-0 p-1 hover:bg-white rounded text-slate-500"
                              title="Baixar .eml da quarentena"
                            >
                              <Download className="h-3 w-3" />
                            </button>
                          )}
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
